import { useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import RichTextViewer from './RichTextViewer.jsx';
import './RichTextEditor.css';

/**
 * 後台用的富文本編輯器（商品詳細說明）。
 *
 * 工具列：標題、粗斜體、刪除線、文字色/底色、對齊、清單、引言、連結、圖片、影片。
 * - 圖片：採「貼網址插入」（本期沒有圖片上傳系統）
 * - 影片：貼影片連結嵌入（YouTube 等）
 * - 清空全部：一鍵清掉所有內容
 * - 預覽：切換成前台真實顯示的樣子（與前台用同一個 RichTextViewer）
 *
 * 產出的是 HTML 字串，前台顯示時務必用 <RichTextViewer> 消毒後再渲染。
 */
function RichTextEditor({ value, onChange, placeholder }) {
  const quillRef = useRef(null);
  const [preview, setPreview] = useState(false);

  // 圖片改成「輸入網址」而非上傳檔案（避免存成超大的 base64）
  const imageByUrlHandler = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const url = window.prompt('請貼上圖片網址（https://...）');
    if (!url) return;
    const range = editor.getSelection(true);
    editor.insertEmbed(range.index, 'image', url, 'user');
    editor.setSelection(range.index + 1);
  };

  // 一鍵清空全部內容
  const clearAll = () => {
    if (!window.confirm('確定清空所有內容？此動作無法復原。')) return;
    const editor = quillRef.current?.getEditor();
    if (editor) editor.setContents([]);
    onChange?.('');
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote'],
        ['link', 'image', 'video'],
        ['clean'],
      ],
      handlers: { image: imageByUrlHandler },
    },
  }), []);

  return (
    <div className="rte">
      <div className="rte-bar">
        <button type="button" className="rte-act" onClick={() => setPreview(p => !p)}>
          {preview ? '← 返回編輯' : '👁 預覽'}
        </button>
        {!preview && (
          <button type="button" className="rte-act danger" onClick={clearAll}>
            清空全部
          </button>
        )}
      </div>

      {preview ? (
        <div className="rte-preview">
          {value ? <RichTextViewer html={value} /> : <p className="rte-preview-empty">（目前沒有內容）</p>}
        </div>
      ) : (
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ''}
          onChange={onChange}
          modules={modules}
          placeholder={placeholder || '輸入商品詳細說明，可貼圖片網址、影片連結…'}
        />
      )}
    </div>
  );
}

export default RichTextEditor;
